const EMPTY_SPECIFICATION_ROW = { key: '', label: '', value: '', unit: '' };

function optionalText(value) {
  const text = String(value ?? '').trim();
  return text || null;
}

function slugKey(value) {
  const key = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return key || 'specification';
}

function coordinate(value, fieldName, minimum, maximum) {
  const text = String(value ?? '').trim();
  if (!text) {
    return null;
  }

  const result = Number(text);
  if (!Number.isFinite(result) || result < minimum || result > maximum) {
    throw new Error(`${fieldName} must be between ${minimum} and ${maximum}.`);
  }
  return result;
}

export function specificationRowsFromAsset(specifications) {
  if (!specifications || typeof specifications !== 'object') {
    return [{ ...EMPTY_SPECIFICATION_ROW }];
  }

  if (Array.isArray(specifications.fields)) {
    const rows = specifications.fields
      .filter((field) => field && typeof field === 'object')
      .map((field) => ({
        key: String(field.key || ''),
        label: String(field.label || field.key || ''),
        value: field.value === null || field.value === undefined ? '' : String(field.value),
        unit: String(field.unit || ''),
      }));
    return rows.length ? rows : [{ ...EMPTY_SPECIFICATION_ROW }];
  }

  const rows = Object.entries(specifications).map(([key, value]) => ({
    key,
    label: key,
    value: value && typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
    unit: '',
  }));
  return rows.length ? rows : [{ ...EMPTY_SPECIFICATION_ROW }];
}

export function assetSettingsFromAsset(asset) {
  const integrationMetadata = asset?.integration_metadata && typeof asset.integration_metadata === 'object'
    ? asset.integration_metadata
    : {};

  return {
    asset_name: asset?.asset_name || '',
    description: asset?.properties?.description || asset?.description || '',
    asset_type: asset?.asset_type || 'physical',
    asset_nature: asset?.asset_nature || 'physical',
    administrative_status: asset?.administrative_status || 'active',
    list_on_capabilities_explorer: Boolean(
      asset?.properties?.list_on_capabilities_explorer
      ?? asset?.properties?.listed_on_capabilities_explorer
      ?? asset?.properties?.is_listed
      ?? asset?.global_response,
    ),
    manufacturer: asset?.manufacturer || '',
    model: asset?.model || '',
    serial_number: asset?.serial_number || '',
    asset_code: asset?.asset_code || asset?.source_capability_asset_id || '',
    asset_image_id: asset?.asset_image_id || '',
    location_label: asset?.location_label || '',
    facility_id: asset?.facility_id || '',
    latitude: asset?.latitude ?? '',
    longitude: asset?.longitude ?? '',
    integration_type: asset?.integration_type || 'not_connected',
    integration_gateway_id: asset?.integration_gateway_id || '',
    amx_connector_id: asset?.amx_connector_id || '',
    telemetry_topic: asset?.telemetry_topic || '',
    actions_topic: asset?.actions_topic || '',
    interaction_model_id: asset?.interaction_model_id || '',
    external_system: integrationMetadata.external_system || '',
    external_endpoint: integrationMetadata.endpoint || '',
    integration_notes: integrationMetadata.notes || '',
    integration_metadata: integrationMetadata,
  };
}

export function buildAssetSettingsPayload(settings, specificationRows, existingSpecifications = null) {
  const assetName = String(settings.asset_name || '').trim();
  if (!assetName) {
    throw new Error('Asset name is required.');
  }
  if (!settings.asset_type) {
    throw new Error('Asset type is required.');
  }

  const latitudeText = String(settings.latitude ?? '').trim();
  const longitudeText = String(settings.longitude ?? '').trim();
  if (Boolean(latitudeText) !== Boolean(longitudeText)) {
    throw new Error('Latitude and Longitude must be provided together.');
  }

  const integrationType = settings.integration_type || 'not_connected';
  const integrationMetadata = settings.integration_metadata && typeof settings.integration_metadata === 'object'
    ? { ...settings.integration_metadata }
    : {};
  delete integrationMetadata.external_system;
  delete integrationMetadata.endpoint;
  delete integrationMetadata.notes;
  if (integrationType === 'external_integration') {
    const externalSystem = optionalText(settings.external_system);
    const externalEndpoint = optionalText(settings.external_endpoint);
    if (externalSystem) integrationMetadata.external_system = externalSystem;
    if (externalEndpoint) integrationMetadata.endpoint = externalEndpoint;
  }
  const integrationNotes = optionalText(settings.integration_notes);
  if (integrationNotes) integrationMetadata.notes = integrationNotes;

  const editableRows = Array.isArray(specificationRows) ? specificationRows : [];
  const fields = editableRows
    .map((row) => ({
      key: slugKey(row.key || row.label),
      label: String(row.label || row.key || '').trim(),
      value: String(row.value ?? '').trim(),
      unit: String(row.unit || '').trim(),
    }))
    .filter((row) => row.label || row.value);
  const specificationsUnchanged = existingSpecifications
    && JSON.stringify(editableRows) === JSON.stringify(specificationRowsFromAsset(existingSpecifications));

  return {
    asset_name: assetName,
    description: optionalText(settings.description),
    asset_type: settings.asset_type,
    asset_nature: settings.asset_nature || 'physical',
    administrative_status: settings.administrative_status || 'active',
    global_response: Boolean(settings.list_on_capabilities_explorer),
    properties: {
      list_on_capabilities_explorer: Boolean(settings.list_on_capabilities_explorer),
    },
    manufacturer: optionalText(settings.manufacturer),
    model: optionalText(settings.model),
    serial_number: optionalText(settings.serial_number),
    asset_code: optionalText(settings.asset_code),
    asset_image_id: optionalText(settings.asset_image_id),
    location_label: optionalText(settings.location_label),
    facility_id: optionalText(settings.facility_id),
    latitude: coordinate(settings.latitude, 'Latitude', -90, 90),
    longitude: coordinate(settings.longitude, 'Longitude', -180, 180),
    specifications: specificationsUnchanged
      ? existingSpecifications
      : (fields.length ? { fields } : {}),
    integration_type: integrationType,
    integration_gateway_id: integrationType === 'amx_gateway'
      ? optionalText(settings.integration_gateway_id)
      : null,
    amx_connector_id: integrationType === 'amx_gateway'
      ? optionalText(settings.amx_connector_id)
      : null,
    telemetry_topic: integrationType === 'amx_gateway'
      ? optionalText(settings.telemetry_topic)
      : null,
    actions_topic: integrationType === 'amx_gateway'
      ? optionalText(settings.actions_topic)
      : null,
    interaction_model_id: integrationType === 'amx_gateway'
      ? optionalText(settings.interaction_model_id)
      : null,
    integration_metadata: integrationMetadata,
  };
}
